import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Prompt } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Input,
  Modal,
  PageHeader,
} from "antd";
import { some, omit } from "lodash";

import { ConfirmDelete } from "common/components/modal/confirmDelete/confirmDelete";
import {
  selectCandidates,
  setUploadCandidate,
} from "redux/slices/candidateSlice";

import { onFilterTextBoxChanged } from "utilities/tableUtility";
import K from "utilities/constants";
import {
  DATE_FIELDS,
  getStatusChangeWarningMessage,
  hasValidationErrors,
  parseAndNormalizeDateValue,
} from "./helpers";
import { useUploadPageData } from "./hooks/useUploadPageData";
import { useUploadModal } from "./hooks/useUploadModal";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import "./previewUpload.scss";

const { Search } = Input;

const toErrorRows = (errors) =>
  typeof errors === "string" ? [[null, errors]] : Object.entries(errors ?? {});

const toErrorMessages = (rowErrors) => {
  if (typeof rowErrors === "string") return [rowErrors];
  if (Array.isArray(rowErrors)) return rowErrors.flatMap(toErrorMessages);
  return Object.values(rowErrors ?? {}).flatMap(toErrorMessages);
};

export default function PreviewUploads() {
  const gridRef = useRef(null);
  const dispatch = useDispatch();

  const { uploadedCandidate } = useSelector(selectCandidates);
  const rowData = useMemo(() => {
    return uploadedCandidate.map((row) => {
      const normalizedRow = { ...row };
      DATE_FIELDS.forEach((field) => {
        const normalized = parseAndNormalizeDateValue(row[field]);
        normalizedRow[field] = normalized.value;
      });
      return normalizedRow;
    });
  }, [uploadedCandidate]);
  const hasData = some(rowData, (row) =>
    some(omit(row, "id"), (value) => value != null && value !== ""),
  );

  const hasErrors = useMemo(() => hasValidationErrors(rowData), [rowData]);
  const { loading, columns } = useUploadPageData();
  const navigationGuard = useNavigationGuard(hasData);
  const uploadModal = useUploadModal(
    uploadedCandidate,
    navigationGuard.allowNavigation,
  );

  const handleSearchChange = useCallback((event) => {
    onFilterTextBoxChanged(event.target.value, gridRef);
  }, []);

  const handleProcessCellForClipboard = useCallback((params) => {
    const { column, node } = params;
    const colDef = column.getColDef();
    if (colDef.cellRenderer && node?.data) {
      return colDef.cellRenderer({
        ...params,
        data: node.data,
        value: params.value,
      });
    }
    return params.value;
  }, []);

  const handleCellValueChanged = () => {
    const updatedRows = [];
    gridRef.current.api.forEachNode((node) => {
      updatedRows.push({ ...node.data });
    });
    dispatch(setUploadCandidate(updatedRows));
  };

  const handleProcessCellFromClipboard = useCallback((params) => {
    const colDef = params.column?.getColDef?.();
    const headerName = colDef?.headerName;
    if (!DATE_FIELDS.includes(headerName)) return params.value;

    const normalized = parseAndNormalizeDateValue(params.value);
    const currentValue = params.node?.data?.[headerName] ?? "";

    if (!normalized.isValid) return currentValue;
    return normalized.value;
  }, []);

  const handleSave = () => {
    const updatedRows = [];
    gridRef.current.api.forEachNode((node) => {
      updatedRows.push(node.data);
    });
    dispatch(setUploadCandidate(updatedRows));
    uploadModal.handleOpen();
  };

  if (loading) return <Empty />;

  return (
    <>
      <PageHeader
        ghost={false}
        title="Uploaded Candidates"
        className="client-ag-table-header"
      />
      <Card
        className="client-ag-table-card"
        title={
          <Search
            className="client-search"
            placeholder="Search"
            allowClear
            onChange={handleSearchChange}
            style={{ width: 264 }}
          />
        }
      >
        <div className="ag-theme-alpine s2-theme-style" style={{ height: 675 }}>
          <AgGridReact
            ref={gridRef}
            rowData={JSON.parse(JSON.stringify(rowData))}
            columnDefs={columns}
            singleClickEdit
            onCellValueChanged={handleCellValueChanged}
            enableGroupEdit
            enableCellChangeFlash
            processCellForClipboard={handleProcessCellForClipboard}
            processCellFromClipboard={handleProcessCellFromClipboard}
            {...K.AgGridTable.DefaultProps}
          />
        </div>
        <div className="client-actions-wrap">
          <Button onClick={navigationGuard.handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!hasData || hasErrors}
          >
            Save
          </Button>
        </div>
      </Card>

      <Modal
        title="Upload CSV File"
        centered
        className="s2-theme-style"
        onOk={uploadModal.handleConfirm}
        onCancel={uploadModal.handleCancel}
        okButtonProps={{
          loading: uploadModal.modalState.loading,
          disabled: !uploadedCandidate,
        }}
        cancelButtonProps={{ disabled: !uploadedCandidate }}
        destroyOnClose
        open={uploadModal.modalState.open}
      >
        <div className="d-flex mt-3 justify-content-center">
          Are you sure you want to upload this file?
        </div>
        <Divider />
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          {Object.entries(uploadModal.modalState.statusChangeWarnings)
            .filter(([, emails]) => emails?.length > 0)
            .map(([statusKey, emails]) => (
              <Alert
                key={statusKey}
                message={getStatusChangeWarningMessage(statusKey)}
                description={
                  <ul>
                    {emails.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                }
                type="warning"
                showIcon
              />
            ))}
          {uploadModal.modalState.errors && (
            <Alert
              message="Errors in CSV:"
              description={
                <ol>
                  {toErrorRows(uploadModal.modalState.errors).map(
                    ([rowKey, rowErrors]) => (
                      <li
                        key={rowKey}
                        style={{ color: "red", fontWeight: "bold" }}
                      >
                        {rowKey !== null && <>Errors in Row {rowKey}:</>}
                        <ul>
                          {toErrorMessages(rowErrors).map((errorMsg) => (
                            <li
                              key={errorMsg}
                              style={{ color: "red", fontWeight: "normal" }}
                            >
                              {errorMsg}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ),
                  )}
                </ol>
              }
              type="error"
              showIcon
            />
          )}
        </div>
      </Modal>

      <Prompt when={hasData} message={navigationGuard.handlePromptMessage} />
      <ConfirmDelete
        title="Discard Upload"
        open={navigationGuard.isDiscardModalVisible}
        closeModal={navigationGuard.handleDiscardCancel}
        onConfirm={navigationGuard.handleDiscardConfirm}
        okText="Discard"
      >
        Are you sure you want to leave? All uploaded candidate data will be
        lost.
      </ConfirmDelete>
    </>
  );
}
